import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import placeEnhancementsService, { PlaceFile } from "@/services/PlaceEnhancementsService"
import moment from "moment"

interface PlaceFilesTabProps {
  placeId: string
  onFilesChange?: () => void
}

const PlaceFilesTab: React.FC<PlaceFilesTabProps> = ({ placeId, onFilesChange }) => {
  const { t } = useTranslation("global")
  const [files, setFiles] = useState<PlaceFile[]>(() => placeEnhancementsService.getFiles(placeId))
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")

  // Form state
  const [fileName, setFileName] = useState("")
  const [fileUrl, setFileUrl] = useState("")
  const [category, setCategory] = useState("General")
  const [notes, setNotes] = useState("")

  const reloadFiles = async () => {
    try {
      setIsLoading(true)
      const list = await placeEnhancementsService.fetchFilesAsync(placeId)
      setFiles(list)
      if (onFilesChange) onFilesChange()
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    reloadFiles()
  }, [placeId])

  const handleOpenAdd = () => {
    setFileName("")
    setFileUrl("")
    setCategory("General")
    setNotes("")
    setShowModal(true)
  }

  const handleFileUploadMock = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFileName(file.name)
      const reader = new FileReader()
      reader.onload = (event) => {
        if (event.target?.result) {
          setFileUrl(event.target.result as string)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fileName.trim()) return

    try {
      setIsUploading(true)
      await placeEnhancementsService.addFileAsync({
        placeId,
        name: fileName,
        url: fileUrl || "#",
        category: category || "General",
        mimeType: fileName.endsWith(".pdf") ? "application/pdf" : fileName.endsWith(".png") || fileName.endsWith(".jpg") ? "image" : "document",
        sizeBytes: 1024 * (Math.floor(Math.random() * 500) + 50)
      })
      setShowModal(false)
      await reloadFiles()
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm(t("Are you sure you want to delete this shared file?"))) {
      await placeEnhancementsService.deleteFileAsync(id)
      await reloadFiles()
    }
  }

  const getFileIcon = (file: PlaceFile) => {
    const name = file.name.toLowerCase()
    if (name.endsWith(".pdf")) return <i className="fas fa-file-pdf text-danger fs-4" />
    if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".webp"))
      return <i className="fas fa-file-image text-primary fs-4" />
    if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv"))
      return <i className="fas fa-file-excel text-success fs-4" />
    if (name.endsWith(".doc") || name.endsWith(".docx"))
      return <i className="fas fa-file-word text-info fs-4" />
    return <i className="fas fa-file-lines text-secondary fs-4" />
  }

  const filteredFiles = files.filter(f => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase()) || (f.category && f.category.toLowerCase().includes(search.toLowerCase()))
    const matchCategory = !selectedCategory || f.category === selectedCategory
    return matchSearch && matchCategory
  })

  return (
    <div className="py-2">
      <div className="d-flex justify-content-end align-items-center mb-3">
        <Button variant="primary" size="sm" className="rounded-3 px-3 fw-semibold" onClick={handleOpenAdd}>
          <i className="fas fa-cloud-arrow-up me-1" />
          {t("Subir / Compartir Archivo")}
        </Button>
      </div>

      <Row className="g-2 mb-3">
        <Col md={7}>
          <Form.Control
            size="sm"
            placeholder={t("Buscar archivo por nombre o categoría...")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col md={5}>
          <Form.Select
            size="sm"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="">-- {t("Todas las Categorías")} --</option>
            <option value="General">General</option>
            <option value="Planos">Planos & Arquitectura</option>
            <option value="Contratos">Contratos & Legales</option>
            <option value="Facturación">Facturación & Presupuestos</option>
            <option value="Técnico">Especificaciones Técnicas</option>
          </Form.Select>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          {t("Cargando archivos compartidos...")}
        </div>
      ) : filteredFiles.length === 0 ? (
        <Card className="border-dashed bg-light text-center py-5">
          <Card.Body>
            <i className="fas fa-folder-open text-muted fs-1 mb-2 d-block" />
            <p className="fw-semibold text-secondary mb-1">{t("No hay archivos compartidos registrados")}</p>
            <small className="text-muted d-block mb-3">{t("Sube planos, contratos o documentos relevantes para este lugar")}</small>
            <Button variant="outline-primary" size="sm" onClick={handleOpenAdd}>
              <i className="fas fa-upload me-1" />
              {t("Subir Primer Archivo")}
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle border shadow-sm rounded">
            <thead className="table-light small">
              <tr>
                <th style={{ width: 50 }}>{t("Tipo")}</th>
                <th>{t("Nombre del Archivo")}</th>
                <th>{t("Categoría")}</th>
                <th>{t("Tamaño")}</th>
                <th>{t("Fecha de Subida")}</th>
                <th className="text-end pe-3">{t("Acciones")}</th>
              </tr>
            </thead>
            <tbody className="small">
              {filteredFiles.map(f => (
                <tr key={f.id}>
                  <td className="text-center">{getFileIcon(f)}</td>
                  <td>
                    <div className="fw-bold text-dark">{f.name}</div>
                    {f.uploadedBy && <small className="text-muted"><i className="fas fa-user me-1" />{f.uploadedBy}</small>}
                  </td>
                  <td>
                    <Badge bg="light" text="dark" className="border">
                      {f.category || "General"}
                    </Badge>
                  </td>
                  <td className="text-muted">
                    {f.sizeBytes ? `${(f.sizeBytes / 1024).toFixed(1)} KB` : "—"}
                  </td>
                  <td className="text-muted">
                    {f.uploadedAt ? moment(f.uploadedAt).format("DD/MM/YYYY HH:mm") : "—"}
                  </td>
                  <td className="text-end pe-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="px-2 py-0 me-1"
                      title={t("Descargar / Abrir")}
                      onClick={() => window.open(f.url, "_blank")}
                    >
                      <i className="fas fa-download" />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="px-2 py-0"
                      title={t("Eliminar")}
                      onClick={() => handleDelete(f.id)}
                    >
                      <i className="fas fa-trash-can" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Modal Subir Archivo */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
              <i className="fas fa-cloud-arrow-up text-primary" />
              {t("Subir o Compartir Archivo")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="small">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Seleccionar Archivo")} *</Form.Label>
                <Form.Control size="sm" type="file" onChange={handleFileUploadMock} />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Nombre / Descripción del Archivo")} *</Form.Label>
                <Form.Control
                  size="sm"
                  required
                  placeholder="ej. Plano_Arquitectonico_Piso_1.pdf"
                  value={fileName}
                  onChange={e => setFileName(e.target.value)}
                />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Categoría del Documento")}</Form.Label>
                <Form.Select size="sm" value={category} onChange={e => setCategory(e.target.value)}>
                  <option value="General">General</option>
                  <option value="Planos">Planos & Arquitectura</option>
                  <option value="Contratos">Contratos & Legales</option>
                  <option value="Facturación">Facturación & Presupuestos</option>
                  <option value="Técnico">Especificaciones Técnicas</option>
                </Form.Select>
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("URL Directa (Opcional)")}</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="https://..."
                  value={fileUrl}
                  onChange={e => setFileUrl(e.target.value)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="primary" size="sm" type="submit" disabled={isUploading} className="w-100 py-2 fw-semibold rounded-3">
              {isUploading ? <Spinner animation="border" size="sm" /> : t("Guardar Archivo")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default PlaceFilesTab
